import { flattenExamQuestions, getAttemptScores, getEvaluationMark, getQuestionEntry } from '../utils/examScores';
/**
 * AnalyticsEngine.js
 * Prepared for Instil Learning Hub - Academic Director Control
 * Updated for Hybrid AI Backend Integration, Skill-Type Analysis & Dynamic Mark-Based Weightage
 * UPGRADES: Added robust zero-division safeguards for Skill Metrics, hardened AI Prompt constraints, 
 * and implemented Token Truncation to prevent 400 Bad Request limits on the AI Backend.
 */

import { checkMCQ, checkShortAnswer, checkMatching } from '../utils/ExamGradingLogic'; // Assuming these exist

export const generateHybridAnalysisContext = (paper, studentAttempts) => {
    // --- STEP 1: PREVENT ATTEMPT INFLATION (BEST ATTEMPT LOGIC) ---
    // Group by examId and pick the best score to ensure cumulative accuracy
    const uniqueExams = {};
    studentAttempts.forEach(attempt => {
        const currentScore = getAttemptScores(attempt).totalScore;
        if (!uniqueExams[attempt.examId] || currentScore > uniqueExams[attempt.examId].totalPerformance) {
            uniqueExams[attempt.examId] = { ...attempt, totalPerformance: currentScore };
        }
    });
    const attemptsToProcess = Object.values(uniqueExams);

    // 1. Group Mistakes by Syllabus Topic
    const topicAnalysis = {};
    
    // --- FEATURE: Skill Category Tracking ---
    const skillAnalysis = {
        calculation: { correct: 0, total: 0, marksScored: 0, marksTotal: 0, accuracy: 0 },
        theory: { correct: 0, total: 0, marksScored: 0, marksTotal: 0, accuracy: 0 },
        analysis: { correct: 0, total: 0, marksScored: 0, marksTotal: 0, accuracy: 0 },
        professional: { correct: 0, total: 0, marksScored: 0, marksTotal: 0, accuracy: 0 }
    };

    // Initialize topics from the Paper Syllabus Map safely
    (paper.sections || []).forEach(section => {
        const key = section.topic || section.name || "Uncategorized";
        topicAnalysis[key] = {
            topic: key,
            skillType: section.skillType || 'general',
            adminAdvice: section.improvementAdvice || "Review general syllabus guidelines.",
            aiModifier: section.aiContextModifier || "",
            totalAttempted: 0,
            incorrectDetails: [],
            marksAllocated: 0,
            marksScored: 0
        };
    });

    // 2. Process all (filtered) attempts to find patterns in mistakes
    attemptsToProcess.forEach(attempt => {
        const questions = attempt.examQuestions || []; 
        const answers = attempt.answers || {};
        const evaluations = attempt.evaluations || {};

        flattenExamQuestions(questions).forEach(({ question: q, index, subIndex }) => {
            const studentAns = getQuestionEntry(answers, index, subIndex, q.id);
            const evaluation = getQuestionEntry(evaluations, index, subIndex, q.id);

            const topicKey = q.syllabusArea || "Uncategorized";
            const skillKey = q.skillType || 'theory'; 
            const qMarks = Number(q.marks) || 0; 
            
            if (!topicAnalysis[topicKey]) {
                topicAnalysis[topicKey] = { 
                    topic: topicKey,
                    totalAttempted: 0, 
                    incorrectDetails: [],
                    skillType: skillKey,
                    adminAdvice: "Review general syllabus guidelines.",
                    aiModifier: "",
                    marksAllocated: 0,
                    marksScored: 0
                };
            }

            topicAnalysis[topicKey].totalAttempted++;
            topicAnalysis[topicKey].marksAllocated += qMarks;
            
            if (skillAnalysis[skillKey]) {
                skillAnalysis[skillKey].total++;
                skillAnalysis[skillKey].marksTotal += qMarks;
            }

            // --- IMPROVED SCORING LOGIC ---
            let awardedMarks = 0;
            let isCorrect = false;

            if (q.type === 'descriptive') {
                awardedMarks = getEvaluationMark(evaluation) ?? 0;
                isCorrect = awardedMarks === qMarks && qMarks > 0; 
            } else {
                isCorrect = verifyCorrectness(q, studentAns); 
                awardedMarks = isCorrect ? qMarks : 0;
            }

            topicAnalysis[topicKey].marksScored += awardedMarks;

            if (isCorrect) {
                if (skillAnalysis[skillKey]) {
                    skillAnalysis[skillKey].correct++;
                    skillAnalysis[skillKey].marksScored += awardedMarks;
                }
            } else {
                topicAnalysis[topicKey].incorrectDetails.push({
                    question: q.text,
                    studentResponse: formatResponse(studentAns),
                    correctResponse: formatResponse(q.correctAnswer),
                    aiEvaluation: evaluation?.text || "No AI feedback available.",
                    marksLost: qMarks - awardedMarks 
                });
                
                if (skillAnalysis[skillKey]) {
                    skillAnalysis[skillKey].marksScored += awardedMarks;
                }
            }
        });
    });

    // --- EDGE CASE SAFEGUARDS: Pre-calculate Skill Accuracies ---
    // Prevents frontend NaN errors if a specific skill wasn't tested in this paper
    Object.keys(skillAnalysis).forEach(key => {
        const skill = skillAnalysis[key];
        skill.accuracy = skill.marksTotal > 0 
            ? Number(((skill.marksScored / skill.marksTotal) * 100).toFixed(1)) 
            : 0;
    });

    // 3. Construct the AI Tutor Prompt
    const reportData = Object.values(topicAnalysis).filter(t => t.totalAttempted > 0);
    
    const totalAttemptedCount = reportData.reduce((acc, curr) => acc + curr.totalAttempted, 0);
    const totalMistakesCount = reportData.reduce((acc, curr) => acc + curr.incorrectDetails.length, 0);
    const totalExamMarks = reportData.reduce((acc, curr) => acc + curr.marksAllocated, 0);
    const totalExamScored = reportData.reduce((acc, curr) => acc + curr.marksScored, 0);

    const overallAccuracy = totalExamMarks > 0 
        ? ((totalExamScored / totalExamMarks) * 100).toFixed(1) 
        : 0;

    // Hardened prompt limits LLM hallucinations (trailing commas, markdown blocks)
    const prompt = `
ACT AS: Senior ACCA Academic Tutor at Instil Learning Hub.
STUDENT: ${attemptsToProcess[0]?.userName || "Student"}
PAPER: ${paper.name}
OVERALL MARK-BASED ACCURACY: ${overallAccuracy}% (${totalExamScored} / ${totalExamMarks} Marks)

SYLLABUS ANALYSIS CONTEXT:
${reportData.filter(d => d.marksScored < d.marksAllocated).map(data => `
TOPIC: ${data.topic}
SKILL CATEGORY: ${data.skillType}
MARKS LOST IN THIS AREA: ${data.marksAllocated - data.marksScored}
ACADEMIC DIRECTOR FOCUS: ${data.aiModifier}
MISTAKES MADE:
${data.incorrectDetails.map(m => `
  - Question: ${m.question}
  - Student's Logic: ${m.studentResponse}
  - Correct Fact: ${m.correctResponse}
  - Marks Lost: ${m.marksLost}
  - Previous Eval: ${m.aiEvaluation}
`).join('\n')}
`).join('\n\n')}

TASK:
1. Analyze why the student is making these specific mistakes (e.g., conceptual gap vs simple numerical error).
2. Provide a concise, 3-sentence encouraging "Tutor Insight" for each topic based on the mistakes.
3. Do NOT repeat the Admin Advice; focus on the logical "why" behind the errors.

CRITICAL: Your response must be a strictly valid JSON object. 
- The keys must be the exact Topic names.
- The values must be the 3-sentence insight strings.
- DO NOT wrap the response in \`\`\`json markdown blocks. 
- DO NOT include trailing commas.
    `.trim();

    return { 
        prompt, 
        topicAnalysis: reportData, 
        skillMetrics: skillAnalysis, 
        globalStats: { 
            accuracy: overallAccuracy, 
            totalQs: totalAttemptedCount, 
            totalMistakes: totalMistakesCount,
            totalPossibleMarks: totalExamMarks,
            totalScoredMarks: totalExamScored
        } 
    };
};

// --- CRITICAL FIX: Sanitize and Truncate responses for AI prompt ---
const formatResponse = (ans) => {
    if (ans === undefined || ans === null) return "(Empty/Skipped)";
    
    let stringifiedAns = "";
    if (typeof ans === 'object') {
        stringifiedAns = JSON.stringify(ans);
    } else {
        stringifiedAns = String(ans);
    }

    // Cap the length to prevent 1-Million Token payload crashes on the backend.
    // The AI only needs the first ~500 characters to understand the context of the mistake.
    const MAX_LENGTH = 500; 
    if (stringifiedAns.length > MAX_LENGTH) {
        return stringifiedAns.substring(0, MAX_LENGTH) + "... [Truncated to prevent AI context overflow]";
    }
    
    return stringifiedAns;
};

// Exam grading check
const verifyCorrectness = (question, answer) => {
    if (!answer && answer !== 0) return false;
    
    if (question.type === 'mcq') {
        if (Array.isArray(question.correctAnswer)) {
            if (!Array.isArray(answer)) return false;
            const sortedAns = [...answer].map(Number).sort((a,b) => a-b);
            const sortedCorrect = [...question.correctAnswer].map(Number).sort((a,b) => a-b);
            return JSON.stringify(sortedAns) === JSON.stringify(sortedCorrect);
        }
        return Number(answer) === Number(question.correctAnswer);
    }
    
    if (question.type === 'short-answer') {
        const student = String(answer).trim().toLowerCase();
        const correct = String(question.correctAnswer).trim().toLowerCase();
        return student === correct;
    }

    if (question.type === 'matching') {
        return JSON.stringify(answer) === JSON.stringify(question.correctAnswer);
    }

    if (question.type === 'dropdown') {
        return String(answer) === String(question.correctAnswer);
    }
    
    if (question.type === 'mcq-grid') {
        const studentAns = answer || {};
        const correctAns = question.correctAnswer || {};
        const rows = question.rows || [];
        return rows.every((_, rIdx) => 
            studentAns[rIdx] !== undefined && Number(studentAns[rIdx]) === Number(correctAns[rIdx])
        );
    }

    if (question.type === 'descriptive') return true;

    return false; 
};